import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Form, FormGroup, Input, Label, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import { media } from '../data/cloudinary';
import { routeUrls } from '../config/routes';
import { register } from '../redux/actions';
import api from '../redux/services/api';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const SignupForm = ({ showLogo, onLogoClick }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const loader = useSelector(state => state.loader);

  // State for dynamic dropdown options from API
  const [dropdownOptions, setDropdownOptions] = useState({
    sets: [],
    departments: [],
    floors: [],
  });
  const [optionsLoading, setOptionsLoading] = useState(true);

  const [browniteInfo, setBrowniteInfo] = useState({
    fullName: '',
    matricNumber: '',
    email: '',
    sex: '',
    department: '',
    set: '',
    floor: '',
    roomNumber: '',
    password: '',
    password2: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [agreeTerms, setAgreeTerms] = useState(false);

  // Fetch dropdown options from API on component mount
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const { data } = await api.call('get', 'settings/dropdown-options');
        setDropdownOptions({
          sets: data.sets || [],
          departments: data.departments || [],
          floors: data.floors || [],
        });
      } catch (error) {
        console.error('Failed to fetch dropdown options:', error);
        // Fallback to empty arrays - form will show empty dropdowns
      } finally {
        setOptionsLoading(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  const handleInput = e => {
    setBrowniteInfo(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };
  const toggleAgreeTerms = e => setAgreeTerms(e.target.checked);

  const handleSubmit = event => {
    event.preventDefault();
    if (!agreeTerms) {
      alert('Please agree to the terms and conditions before signing up.');
      return;
    }
    dispatch(register(browniteInfo));
  };

  return (
    <Form onSubmit={handleSubmit}>
      {showLogo && (
        <div className="text-center pb-4">
          <img
            src={media.logo}
            className="rounded"
            style={{ width: 60, height: 60, cursor: 'pointer' }}
            alt="logo"
            onClick={onLogoClick}
          />
        </div>
      )}

      <FormGroup>
        <Label for="fullName">Full Name</Label>
        <Input
          type="text"
          name="fullName"
          id="fullName"
          value={browniteInfo.fullName}
          onChange={handleInput}
          placeholder="LAST NAME, FirstName MiddleName"
        />
      </FormGroup>
      <FormGroup>
        <Label for="matricNumber">Matric Number</Label>
        <Input
          type="number"
          id="matricNumber"
          value={browniteInfo.matricNumber}
          onChange={handleInput}
          name="matricNumber"
          placeholder="Your matriculation number"
        />
      </FormGroup>
      <FormGroup>
        <Label for="email">Email</Label>
        <Input
          type="email"
          id="email"
          value={browniteInfo.email}
          onChange={handleInput}
          name="email"
          placeholder="Your email address"
        />
      </FormGroup>
      <FormGroup>
        <Label for="sex">Sex</Label>
        <Input
          type="select"
          name="sex"
          id="sex"
          value={browniteInfo.sex}
          onChange={handleInput}
        >
          <option value="">Select Your Sex</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </Input>
      </FormGroup>
      <FormGroup>
        <Label for="department">Department</Label>
        <Input
          type="select"
          name="department"
          id="department"
          value={browniteInfo.department}
          onChange={handleInput}
          disabled={optionsLoading}
        >
          <option value="">Select Your Department</option>
          {dropdownOptions.departments.map((dept) => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </Input>
      </FormGroup>
      <FormGroup>
        <Label for="set">Set/Level</Label>
        <Input
          type="select"
          name="set"
          id="set"
          value={browniteInfo.set}
          onChange={handleInput}
          disabled={optionsLoading}
        >
          <option value="">Select Your Set</option>
          {dropdownOptions.sets.map((set) => (
            <option key={set} value={set}>{set}</option>
          ))}
        </Input>
      </FormGroup>
      <FormGroup>
        <Label for="floor">Block/Floor</Label>

        <Input
          type="select"
          name="floor"
          id="floor"
          value={browniteInfo.floor}
          onChange={handleInput}
          disabled={optionsLoading}
        >
          <option value="">Select Your Block/Floor</option>
          {dropdownOptions.floors.map((floor) => (
            <option key={floor} value={floor}>{floor}</option>
          ))}
        </Input>
      </FormGroup>
      {browniteInfo.floor && !browniteInfo.floor.includes('OffCampus') && (
        <FormGroup>
          <Label for="roomNumber">Room Number</Label>
          <Input
            type="number"
            name="roomNumber"
            id="roomNumber"
            value={browniteInfo.roomNumber}
            onChange={handleInput}
            placeholder="Your room number (e.g., 12)"
          />
        </FormGroup>
      )}
      <FormGroup>
        <Label for="password">Password</Label>
        <div className="input-group">
          <Input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            value={browniteInfo.password}
            onChange={handleInput}
            placeholder="Your password (> 6 characters)"
            className="form-control"
          />
          <div className="input-group-append">
            <span
              className="input-group-text"
              onClick={() => setShowPassword(!showPassword)}
              style={{ cursor: 'pointer' }}
            >
              {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </span>
          </div>
        </div>
      </FormGroup>

      <FormGroup>
        <Label for="password2">Confirm password</Label>
        <div className="input-group">
          <Input
            type={showConfirmPassword ? "text" : "password"}
            id="password2"
            name="password2"
            value={browniteInfo.password2}
            onChange={handleInput}
            placeholder="Confirm password"
            className="form-control"
          />
          <div className="input-group-append">
            <span
              className="input-group-text"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ cursor: 'pointer' }}
            >
              {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </span>
          </div>
        </div>
      </FormGroup>


      <FormGroup check>
        <Label check>
          <Input
            type="checkbox"
            value={agreeTerms}
            onChange={toggleAgreeTerms}
          />{' '}
          I agree to the terms and policy
        </Label>
      </FormGroup>
      <hr />
      <FormGroup style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          size="lg"
          className="bg-gradient-theme-left border-0"
          block
          onClick={handleSubmit}
        >
          Signup{' '}
          {loader && loader.register && <Spinner color="light" size="sm" />}
        </Button>
      </FormGroup>
      <div className="text-center pt-1">
        <h6>or</h6>
        <h6>
          <p
            onClick={() => history.push(routeUrls.login)}
            style={{ cursor: 'pointer' }}
          >
            Login
          </p>
        </h6>
      </div>
    </Form >
  );
};

SignupForm.propTypes = {
  showLogo: PropTypes.bool,
  onLogoClick: PropTypes.func,
};

SignupForm.defaultProps = {
  showLogo: true,
  onLogoClick: () => { },
};
export default SignupForm;
